import { Button, Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CheckoutLayout } from '../../components/checkout/CheckoutLayout'
import { AttachedBlocks, useAttachedBlocks } from '../../components/cms/AttachedBlocks'
import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { formatPrice } from '../../utils/formatPrice'
import { buildOgMeta } from '../../utils/og'
import { longFormRichTextClassName } from '../../utils/richText'
import { withoutCmsBlocks } from '../../utils/withoutCmsBlocks'

const CareExaminationDetailsPage = () => {
    const { serviceId } = Route.useParams()
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()

    const { data: service, isLoading } = api.careExamination.getPublicService.useQuery({
        id: serviceId,
    })

    const hasAttachedBlocks = useAttachedBlocks(service?.cmsBlocks).length > 0
    const longDescriptionHtml = service?.longDescription
        ? getLocalizedValue(service.longDescription)
        : ''

    return (
        <CheckoutLayout>
            <section className="mx-auto w-full max-w-4xl px-6 py-16">
                <Link
                    className="mb-8 inline-flex items-center gap-1 text-sm font-medium text-brand-gray transition-colors hover:text-primary"
                    to="/booking/examination"
                >
                    <ChevronLeft className="h-4 w-4" />
                    {t('frontend.catalog.backToExaminationBooking')}
                </Link>

                {isLoading ? (
                    <div>
                        <Skeleton className="mb-4 h-8 w-64" />
                        <Skeleton className="mb-8 h-6 w-32" />
                        <Skeleton className="h-40 w-full" />
                    </div>
                ) : !service ? (
                    <div className="py-24 text-center">
                        <p className="text-lg text-brand-gray">
                            {t('frontend.catalog.careExaminationNotFound')}
                        </p>
                    </div>
                ) : (
                    <div>
                        <h1 className="mb-2 text-4xl font-bold text-brand-dark">
                            {getLocalizedValue(service.name)}
                        </h1>
                        {service.description && (
                            <p className="mb-6 text-lg text-brand-gray">
                                {getLocalizedValue(service.description)}
                            </p>
                        )}

                        {service.price != null && (
                            <p className="mb-8 text-2xl font-bold text-primary">
                                {formatPrice(service.price)}
                            </p>
                        )}

                        <div className="mb-10">
                            {service.isBookable ? (
                                <Button asChild size="lg">
                                    <Link
                                        to="/booking/examination"
                                        search={{ serviceId: service.id }}
                                    >
                                        {t('frontend.catalog.bookNow')}
                                    </Link>
                                </Button>
                            ) : (
                                <Button disabled size="lg">
                                    {t('frontend.catalog.nothingAvailable')}
                                </Button>
                            )}
                        </div>

                        {(hasAttachedBlocks || longDescriptionHtml) && (
                            <div className="border-t border-slate-100 pt-8">
                                <h2 className="mb-4 text-xl font-bold text-brand-dark">
                                    {t('frontend.catalog.description')}
                                </h2>
                                <AttachedBlocks blocks={service.cmsBlocks} className="mb-6" />
                                {longDescriptionHtml && (
                                    <div
                                        className={longFormRichTextClassName}
                                        dangerouslySetInnerHTML={{ __html: longDescriptionHtml }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </CheckoutLayout>
    )
}

export const Route = createFileRoute('/care-examinations/$serviceId')({
    component: CareExaminationDetailsPage,
    loader: async ({ context, params }) => {
        const [service, websiteSettings] = await Promise.allSettled([
            context.apiUtils.careExamination.getPublicService.ensureData({ id: params.serviceId }),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            service:
                service.status === 'fulfilled' && service.value
                    ? withoutCmsBlocks(service.value)
                    : null,
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
            serviceId: params.serviceId,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: loaderData?.service?.ogTitle || 'Long-Y - Járóbeteg-ellátás',
            description: loaderData?.service?.ogDescription,
            imageId: loaderData?.service?.ogImageId,
            url: `${import.meta.env.VITE_WEB_URL || ''}/care-examinations/${loaderData?.serviceId ?? ''}`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
